import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, forkJoin, Observable } from "rxjs";
import { map, switchMap, tap } from "rxjs/operators";
import { OrdersCommunicationService } from "src/app/_shared/services/orders.service";
import { RequestsService } from "src/app/_shared/services/requests.service";
import { formatDatesRanges } from "src/app/_shared/utils/formatDates";
import { environment } from "src/environments/environment";
import {
  FarmerAngularFireDatabase,
  FarmerFlutterFireDatabase,
} from "../../usage/firebase";
// import { FarmerFlutterFireDatabase } from "../../usage/firebase";
import {
  BulkOrderUpdate,
  DeepPartial,
  InitiatePaymentRequest,
  InitiatePaymentResponse,
  Order,
  OrderLine,
  OrderPurchaseItem,
  OrderReturnedItemsRequest,
  OrderSummary,
  PaymentStatusResponse,
} from "../agrishop.interface";

@Injectable({
  providedIn: "root",
})
export class OrderService {
  currentOrder$: BehaviorSubject<Order> = new BehaviorSubject(null);
  summaryDateRange: Date[];
  orderSummary$: BehaviorSubject<OrderSummary> = new BehaviorSubject(null);
  constructor(
    private requestService: RequestsService,
    private httpService: HttpClient,
    private orderCommunService: OrdersCommunicationService,
    private farmerdb: FarmerAngularFireDatabase,
    private farmerflutterdb: FarmerFlutterFireDatabase
  ) {
    this.summaryDateRange = this.getWeekRange();
    this.getOrderStats();
  }
  returnOrderItem(
    id: string,
    data: OrderReturnedItemsRequest
  ): Observable<{ data: Order }> {
    return this.requestService.dash_patch(`orders/${id}/return`, data, true);
  }

  patchOrder(
    id: string,
    data: DeepPartial<Order>
  ): Observable<{ data: Order }> {
    return this.requestService.dash_patch(`orders/${id}`, data, true).pipe(
      tap((val) => {
        this.orderCommunService.emitChange({ obj: val });
        this.currentOrder$.next(val);
      })
    );
  }
  updateOrder(id: string, data: Partial<Order>): Observable<{ data: Order }> {
    return this.requestService.dash_put(`orders/${id}`, data, true).pipe(
      tap((val) => {
        this.orderCommunService.emitChange({ obj: val });
        this.currentOrder$.next(val);
      })
    );
  }

  purchaseOrderItem(
    id: string,
    data: OrderPurchaseItem
  ): Observable<{ data: Order }> {
    return this.requestService
      .dash_patch(`orders/${id}/purchase`, data, true)
      .pipe(
        tap((val) => {
          this.orderCommunService.emitChange({ obj: val });
          this.currentOrder$.next(val);
        })
      );
  }

  updateOrderLineItem(
    id: OrderLine["id"],
    sku: OrderLine["sku"],
    data: Partial<OrderLine>
  ): Observable<{ data: Order }> {
    return this.requestService
      .dash_patch(`orders/${id}/${sku}`, data, true)
      .pipe(
        tap((val) => {
          this.orderCommunService.emitChange({ obj: val });
          this.currentOrder$.next(val);
        })
      );
  }

  bulkPatchOrder(data: BulkOrderUpdate): Observable<{ data: Order[] }> {
    return this.requestService.dash_patch(`orders`, data, true);
  }

  getOrders(params: string): Observable<{ data: Order[] }> {
    return this.requestService.dash_get3(
      `input_orders/fetch_orders?${params}`,
      null,
      true
    );
  }

  getFirebaseOrders(params: string) {
  }

  downloadAll(params: string): Observable<{ data: Order[] }> {
    console.log("The params" + params);
    return this.requestService.dash_post(`orders/export`, params, true, {
      responseType: "text",
    });
  }

  getOrderStats(): Observable<{ data: OrderSummary }> {
    const dateRange = formatDatesRanges(this.summaryDateRange);
    const queryParams = new URLSearchParams(dateRange).toString();
    const prevValue = this.orderSummary$.getValue();
    this.orderSummary$.next(null);
    return this.requestService
      .dash_get(`orders/stats?${queryParams}`, {}, true)
      .pipe(
        tap(
          (value) => {
            this.orderSummary$.next(this.formatSummaryData(value.data));
          },
          (_err) => {
            this.orderSummary$.next(this.formatSummaryData(prevValue));
          }
        )
      );
  }

  formatSummaryData(data: OrderSummary): OrderSummary {
    const input_categories_sum = data.input_categories.reduce(
      (prev, curr) => curr.total + prev,
      0
    );
    data.input_categories = data.input_categories.map((input) => {
      return {
        ...input,
        percentage: (input.total / input_categories_sum) * 100,
      };
    });

    const inputs_sum = data.ordered_items.reduce(
      (prev, curr) => curr.orders + prev,
      0
    );
    data.ordered_items = data.ordered_items.map((input) => {
      return {
        ...input,
        percentage: (input.orders / inputs_sum) * 100,
      };
    });
    if (data.ordered_items.length > 0) {
      data.top_product = data.ordered_items[0];
    }

    return data;
  }

  createOrder(
    order: Order & { create_new_user?: boolean }
  ): Observable<{ data: Order }> {
    return this.requestService.dash_post(`orders`, order, true).pipe(
      tap((val) => {
        this.orderCommunService.emitChange({ new: true });
        this.currentOrder$.next(val);
      })
    );
  }

  loadSingleOrder(id: string) {
    return this.requestService.dash_get(`orders/${id}`, {}, true).pipe(
      map((data) => data.data),
      tap((data) => {
        this.currentOrder$.next(data);
      })
    );
  }

  generateDeliveryNote(id: string): Observable<{ data: Order }> {
    return this.requestService.dash_post(
      `orders/${id}/delivery_note`,
      null,
      true
    );
  }

  approveDeliveryNote(id: string): Observable<{ data: Order }> {
    return this.requestService.dash_post(
      `orders/${id}/approve_delivery_note`,
      null,
      true
    );
  }
  public get orderSummary() {
    return this.orderSummary$.asObservable();
  }

  getWeekRange() {
    var curr = new Date();
    var first = curr.getDate() - curr.getDay();
    var last = first + 6;
    var firstDay = new Date(curr.setDate(first));
    var lastDay = new Date(curr.setDate(last));
    return [firstDay, lastDay];
  }

  public initiatePayment(
    orderId: string,
    data: InitiatePaymentRequest
  ): Observable<InitiatePaymentResponse> {
    return this.requestService
      .dash_put(`orders/${orderId}/initiate-payment`, data, true)
      .pipe(map((res: InitiatePaymentResponse) => res));
  }

  public getPaymentStatus(order_id: string): Observable<PaymentStatusResponse> {
    return this.httpService
      .get(`${environment.API_URL}api/v2/orders/${order_id}/payment-status`, {
        // add no cache headers
        headers: new HttpHeaders({
          "Cache-Control": "no-cache, no-store, must-revalidate, max-age=0",
        }),
      })
      .pipe(map((res: PaymentStatusResponse) => res));
  }
}
